import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Button component with SZU design system styling
 *
 * Design values:
 * - Primary Blue: #0099D8 (HSL: 197 100% 42%)
 * - Primary Hover: #0077A8
 * - Danger Red: #DC3545
 * - Border Radius: rounded-md (6px) for buttons
 * - Focus Ring: Blue focus ring with offset
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Default: SZU primary blue background, white text
        default:
          'bg-primary text-primary-foreground shadow-sm hover:bg-primary-600 active:bg-primary-700',
        // Destructive: SZU danger red
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-danger-600 active:bg-danger-700',
        // Outline: Blue border, blue text, transparent background
        outline:
          'border-2 border-primary bg-transparent text-primary hover:bg-primary-50 hover:text-primary-600 active:bg-primary-100',
        // Secondary: Gray background
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-gray-200 active:bg-gray-300',
        // Ghost: Transparent with hover state
        ghost: 'hover:bg-gray-100 hover:text-gray-900 active:bg-gray-200',
        // Link: Blue text, underline on hover
        link: 'text-primary underline-offset-4 hover:underline hover:text-primary-600',
        // Success: Green button for positive actions
        success: 'bg-success text-white shadow-sm hover:bg-success-600 active:bg-success-700',
      },
      size: {
        // Small: 6px 12px padding, 14px font
        sm: 'h-9 px-3 text-sm',
        // Default: 10px 20px padding, 16px font
        default: 'h-10 px-5 py-2.5',
        // Large: 12px 24px padding, 18px font
        lg: 'h-11 px-6 text-lg',
        // Icon: Square button for icons only
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
